import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { selectPropertyFieldRules } from "@/features/survey/surveySelectors";
import { useUpdatePropertyMutation } from "@/services/survey-properties";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldInputV2 } from "@emerald-react/input-v2";
import { usePropertyFieldUpdate } from "@hooks/usePropertyFieldUpdate";
import { CustomFieldInputProps } from "@shared/types/component.types";
import { selectCurrentProperty } from "@survey/surveySelectors";
import {
  updateProperty as updatePropertyAction,
  updatePropertyFieldRule,
} from "@survey/surveySlice";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import AddOrEditCustomFieldInput from "./AddOrEditCustomFieldInput";

const CustomFieldInput = (props: CustomFieldInputProps) => {
  const { eachField, activeFieldRuleList, propertyId } = props;
  const dispatch = useAppDispatch();
  const [enableEdit, isEnableEdit] = useState(false);
  const currentProperty = useAppSelector(selectCurrentProperty);
  const propertyFieldRules = useAppSelector(selectPropertyFieldRules);
  const [updatePropertyMutation] = useUpdatePropertyMutation();
  const [customFieldValue, setCustomFieldValue] = useState(eachField?.value);
  const { updateFieldRule } = usePropertyFieldUpdate();

  useEffect(() => {
    setCustomFieldValue(eachField?.value ?? "");
  }, [eachField?.id, eachField?.value]);

  const updateCustomField = (
    e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>,
  ) => {
    const value = e.target.value.trim();

    // If the user focused a "—" field without entering a value, restore the
    // dash display and skip the PATCH call entirely.
    if (value === "" && eachField?.value === "\u2014") {
      setCustomFieldValue("\u2014");
      return;
    }
    setCustomFieldValue(value);

    if (value !== eachField?.value) {
      const updatedCustomFields = currentProperty?.customFields.map((field) => {
        if (field.customField.customFieldId === eachField.id) {
          return {
            ...field,
            value,
            customField: {
              ...field.customField,
              customFieldId: eachField.id,
            },
          };
        }

        return field;
      }) ?? [
        {
          customField: {
            customFieldId: eachField.id,
          },
          value,
        },
      ];

      dispatch(
        updatePropertyAction({
          updates: { customFields: updatedCustomFields },
          propertyId,
        }),
      );
      updatePropertyMutation({
        updates: {
          customFields: [
            {
              customField: {
                customFieldId: eachField.id,
              },
              value,
            },
          ],
        },
        propertyId,
      });

      // Update field rule if value is not empty
      if (value) {
        const matchingFieldRule = propertyFieldRules.find(
          (fieldRule) =>
            fieldRule.fieldKey === eachField.label ||
            fieldRule.fieldLabel === eachField.label ||
            fieldRule.fieldKey?.toLowerCase() ===
              eachField.label.toLowerCase() ||
            fieldRule.fieldLabel?.toLowerCase() ===
              eachField.label.toLowerCase(),
        );

        if (matchingFieldRule?.isHidden) {
          dispatch(
            updatePropertyFieldRule({
              fieldId: matchingFieldRule.fieldId,
              isHidden: false,
              status: "ACTIVE",
              order: activeFieldRuleList.length, // Set order to the end of the active list
            }),
          );
        }

        updateFieldRule(eachField?.label, value);
      }
    }
  };

  const handleFocus = () => {
    // When the user focuses a "—" field, clear it so they can type immediately.
    if (customFieldValue === "\u2014") {
      setCustomFieldValue("");
    }
  };

  return (
    <>
      {enableEdit ? (
        <AddOrEditCustomFieldInput
          eachField={eachField}
          onClose={() => isEnableEdit(false)}
        />
      ) : (
        <div
          className="relative custom-field-with-edit-icon"
          key={eachField?.id}
        >
          <EmeraldInputV2
            type="textarea"
            label={
              eachField.label
                ? eachField.label.charAt(0).toUpperCase() +
                  eachField.label.slice(1)
                : ""
            }
            value={customFieldValue}
            size="small"
            className={classNames("without-label", {
              boldLabel: activeFieldRuleList?.includes(eachField?.label),
            })}
            isValid={true}
            onFocus={handleFocus}
            onBlur={updateCustomField}
            onChange={(e) => {
              setCustomFieldValue(e.target.value);
            }}
          />
          <EmeraldIcon
            icon="edit"
            type="filled"
            variant="icon"
            className="absolute top-2 right-2 cursor-pointer text-base"
            onClick={() => isEnableEdit(true)}
          />
        </div>
      )}
    </>
  );
};

export default CustomFieldInput;
